/**
 * Account-level display & accessibility preferences — the app's copy of the
 * canonical definition (allowed values + defaults).
 *
 * MIRRORS flowdesk-api/src/utils/display-preferences.js, which is the source
 * of truth and validates every write. The app cannot import across repos, so
 * the API's tests/display-preferences.test.js loads THIS file and fails if the
 * two drift apart. Change both together.
 *
 * Pure module — no React, no DOM, no storage — so it is safe to import from
 * anywhere (and from the API's parity test under plain Node).
 */
const def = (values, dflt, extra = {}) =>
  Object.freeze({ values: Object.freeze(values), default: dflt, ...extra })

export const DISPLAY_PREFERENCES = Object.freeze({
  theme: def(['system', 'light', 'dark'], 'system'),
  textSize: def(['compact', 'normal', 'large', 'larger', 'largest'], 'normal'),
  rowDensity: def(['compact', 'comfortable', 'relaxed'], 'comfortable'),
  rowsPerPage: def([25, 50, 100, 200, 500], 25),
  reduceMotion: def(['system', 'on', 'off'], 'system'),
  contrast: def(['system', 'on', 'off'], 'system'),
  underlineLinks: def(['system', 'on', 'off'], 'system'),
  // Region & formats — how things are WRITTEN for this person; every instant
  // stays stored in UTC. Besides 'company', any IANA zone is a valid timezone.
  timezone: def(['company'], 'company', { anyTimeZone: true }),
  dateFormat: def(['dmy', 'mdy', 'iso', 'long'], 'dmy'),
  timeFormat: def(['12', '24'], '12'),
  weekStart: def(['mon', 'sun'], 'mon'),
  numberFormat: def(['comma', 'dot', 'space'], 'comma'),
})

/** IANA timezone name this runtime knows; offsets and lowercase spellings are
 *  rejected (same rule as the API's `isIanaTimeZone`). */
export function isIanaTimeZone(value) {
  if (typeof value !== 'string' || value.length > 64) return false
  if (!/^[A-Z][A-Za-z0-9_+-]*(\/[A-Z][A-Za-z0-9_+-]*){0,2}$/.test(value)) return false
  try {
    new Intl.DateTimeFormat('en', { timeZone: value })
    return true
  } catch {
    return false
  }
}

export const DISPLAY_PREFERENCE_KEYS = Object.freeze(Object.keys(DISPLAY_PREFERENCES))

/** The canonical defaults as one object — what "Reset to defaults" previews. */
export const DISPLAY_DEFAULTS = Object.freeze(
  Object.fromEntries(DISPLAY_PREFERENCE_KEYS.map((k) => [k, DISPLAY_PREFERENCES[k].default])),
)

export function isValidDisplayValue(key, value) {
  const d = DISPLAY_PREFERENCES[key]
  if (!d) return false
  if (d.values.includes(value)) return true
  return d.anyTimeZone === true && isIanaTimeZone(value)
}

/** Every key resolved: the given value when valid, else the default. */
export function resolveDisplayPreferences(stored) {
  const out = {}
  for (const key of DISPLAY_PREFERENCE_KEYS) {
    const v = stored?.[key]
    out[key] = isValidDisplayValue(key, v) ? v : DISPLAY_PREFERENCES[key].default
  }
  return out
}

/** Keys whose value differs between two resolved preference objects. */
export function changedDisplayKeys(a, b) {
  return DISPLAY_PREFERENCE_KEYS.filter((k) => a?.[k] !== b?.[k])
}

/** Text-size level → the `--font-size-scale` multiplier index.css applies.
 *  Kept here only for the preview samples; the CSS owns the real values. */
export const TEXT_SIZE_SCALE = Object.freeze({
  compact: 0.85,
  normal: 1,
  large: 1.15,
  larger: 1.3,
  largest: 1.5,
})

/** The header text-size menu's old browser-only levels → account levels. */
export const LEGACY_FONT_SIZE_LEVELS = Object.freeze({
  compact: 'compact',
  medium: 'normal',
  large: 'large',
})
